import { ColumnDef, Row } from "@tanstack/react-table";
import Avatar from "@/components/Avatar";
import AvatarDetails from "@/components/AvatarDetails";
import { ReactNode } from "react";
import { SortFieldType } from "./sortUtils";

interface BuildAvatarDetailsColumnProps<T> {
  isCompactLayout: boolean;
  getTitle: (row: T) => string;
  getSubtitle?: (row: T) => string | ReactNode;
  subtitleLabel?: string;
  getPicture?: (row: T) => string | undefined;
  additionalMobileFields?: (row: T) => ReactNode;
  header?: string;
  size?: number;
  minSize?: number;
  id?: string;
  sortType?: SortFieldType;
  adjustForTableDetailsPanel?: boolean;
  isFirstColumn?: boolean;
}

/**
 * Builds responsive avatar + details columns
 * Desktop: Single AvatarDetails column
 * Mobile: Separate avatar + stacked info columns (exceptional columns pattern)
 *
 * @example
 * ```tsx
 * const columns = [
 *   ...buildAvatarDetailsColumn({
 *     isCompactLayout,
 *     getTitle: (row) => row.name,
 *     getSubtitle: (row) => row.pgaId,
 *     subtitleLabel: "PGA ID", // Shows "PGA ID: A12345" on mobile
 *     getPicture: (row) => row.profilePhoto,
 *     sortType: "text" // Auto-enables sorting (desktop + mobile)
 *   }),
 *   // ... other columns
 * ];
 * // Note: sortType presence automatically enables sorting (desktop + mobile)
 * ```
 */
export function buildAvatarDetailsColumn<T>({
  isCompactLayout,
  getTitle,
  getSubtitle,
  subtitleLabel,
  getPicture,
  additionalMobileFields,
  header = "Name",
  size = 300,
  minSize,
  id = "personInfo",
  sortType,
  adjustForTableDetailsPanel = false,
  isFirstColumn = true
}: BuildAvatarDetailsColumnProps<T>): ColumnDef<T>[] {
  if (isCompactLayout && isFirstColumn) {
    return [
      {
        id: "avatar",
        header: "",
        cell: ({ row }: { row: Row<T> }) => {
          const picture = getPicture?.(row.original);
          return <Avatar picture={picture} />;
        },
        size: 48,
        minSize: 48
      },
      {
        id,
        header: "",
        cell: ({ row }: { row: Row<T> }) => {
          const subtitle = getSubtitle?.(row.original);
          return (
            <div className="flex-1 flex flex-col gap-1">
              <span className="text-body-medium-semibold">{getTitle(row.original)}</span>
              {getSubtitle ? (
                <div className="flex items-center gap-2 text-body-small text-sys-brand-on-surface-variant">
                  {subtitleLabel && <span>{subtitleLabel}:</span>}
                  <span>{subtitle || <span className="opacity-20">--</span>}</span>
                </div>
              ) : null}
              {additionalMobileFields?.(row.original)}
            </div>
          );
        },
        accessorFn: (row: T) => getTitle(row),
        meta: {
          ...(sortType ? { sortType, sortLabel: header } : { enableSorting: false }),
          className: adjustForTableDetailsPanel ? "-ml-8" : undefined
        }
      }
    ];
  }

  return [
    {
      header: isCompactLayout ? "" : header,
      id,
      cell: ({ row }: { row: Row<T> }) => {
        const subtitle = getSubtitle?.(row.original);
        return (
          <AvatarDetails
            picture={getPicture?.(row.original)}
            title={getTitle(row.original)}
            text={getSubtitle ? subtitle || <span className="opacity-20">--</span> : ""}
          />
        );
      },
      size,
      minSize: minSize ?? size,
      accessorFn: (row: T) => getTitle(row),
      ...(sortType ? { meta: { sortType, sortLabel: header } } : { enableSorting: false })
    }
  ];
}
